// src/components/agencies/AgencyList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchAgencies,
  deleteAgency,
  toggleAgencyStatus,
  clearAgencyError,
  clearAgencySuccess,
  setAgencyFilters,
} from '../../store/actions/agencyActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import FilterBar from '../common/FilterBar';

const AgencyList = () => {
  const dispatch = useDispatch();
  const { agencies, loading, error, success, meta, filters } = useSelector(
    (state) => state.agencies
  );

  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  // Use ref to track initial mount
  const initialMount = useRef(true);
  // Use ref to track if filters have changed
  const filtersRef = useRef(filters);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    locationButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    locationButtonHover: {
      backgroundColor: '#059669',
    },
    locationButtonActive: {
      backgroundColor: '#059669',
    },
    bulkDeleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    bulkDeleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    bulkDeleteButtonDisabled: {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
    locationFilterBar: {
      marginBottom: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    locationInputs: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
      alignItems: 'end',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    label: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      marginBottom: '0.25rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    input: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
    },
    applyLocationButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      height: '38px',
    },
    filterBar: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellCheckbox: {
      padding: '0.75rem 1.5rem',
      width: '40px',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellCheckbox: {
      padding: '1rem 1.5rem',
      width: '40px',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    agencyInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    agencyAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '0.375rem',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#4b5563',
    },
    agencyName: {
      fontWeight: 500,
      color: '#111827',
    },
    entityName: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    openBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    closedBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    stars: {
      color: '#fbbf24',
    },
    reviewCount: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    location: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    distance: {
      fontSize: '0.75rem',
      color: '#10b981',
      fontWeight: 500,
    },
    services: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.25rem',
    },
    serviceTag: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    servicesButton: {
      color: '#0891b2',
    },
    servicesButtonHover: {
      color: '#0e7490',
    },
    hoursButton: {
      color: '#b45309',
    },
    hoursButtonHover: {
      color: '#92400e',
    },
    toggleButton: {
      color: '#059669',
    },
    toggleButtonHover: {
      color: '#047857',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
  };

  // Memoize fetch function
  const fetchAgenciesData = useCallback(() => {
    dispatch(fetchAgencies(filters));
  }, [dispatch, filters]);

  // Only fetch when filters actually change
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchAgenciesData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchAgenciesData();
      }
    }
  }, [fetchAgenciesData, filters]);

  // Cleanup function
  useEffect(() => {
    return () => {
      dispatch(clearAgencyError());
      dispatch(clearAgencySuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    dispatch(setAgencyFilters({
      ...filters,
      search: searchTerm,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch(setAgencyFilters({
      ...filters,
      ...newFilters,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handlePageChange = useCallback((page) => {
    dispatch(setAgencyFilters({
      ...filters,
      page,
    }));
  }, [dispatch, filters]);

  const handleDeleteClick = useCallback((agency) => {
    setSelectedAgency(agency);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteAgency(selectedAgency.id));
      setShowDeleteConfirm(false);
      setSelectedAgency(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedAgency]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedAgency(null);
  }, []);

  const handleToggleStatus = useCallback(async (agency) => {
    try {
      await dispatch(toggleAgencyStatus(agency.id));
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  }, [dispatch]);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(agencies.map(agency => agency.id));
    } else {
      setSelectedIds([]);
    }
  }, [agencies]);

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  }, [selectedIds]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    try {
      // await dispatch(bulkDeleteAgencies(selectedIds));
      setShowBulkDeleteConfirm(false);
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  }, []);

  const handleGetUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, long: longitude });
          setShowLocationFilter(true);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Impossible d\'obtenir votre position. Veuillez entrer les coordonnées manuellement.');
        }
      );
    } else {
      alert('La géolocalisation n\'est pas supportée par votre navigateur.');
    }
  }, []);

  const handleLocationApply = useCallback(() => {
    if (userLocation) {
      dispatch(setAgencyFilters({
        ...filters,
        lat: userLocation.lat,
        long: userLocation.long,
        page: 1,
      }));
    }
  }, [dispatch, filters, userLocation]);

  const handleClearLocation = useCallback(() => {
    setUserLocation(null);
    dispatch(setAgencyFilters({
      ...filters,
      lat: undefined,
      long: undefined,
      radius: 10,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handleClearError = useCallback(() => {
    dispatch(clearAgencyError());
  }, [dispatch]);

  const handleClearSuccess = useCallback(() => {
    dispatch(clearAgencySuccess());
  }, [dispatch]);

  if (loading && !agencies.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des agences</h1>
        <div style={styles.headerActions}>
          <button
            onClick={handleGetUserLocation}
            style={{
              ...styles.locationButton,
              ...(filters.lat ? styles.locationButtonActive : {}),
            }}
            onMouseEnter={(e) => {
              if (!filters.lat) {
                e.currentTarget.style.backgroundColor = styles.locationButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!filters.lat) {
                e.currentTarget.style.backgroundColor = styles.locationButton.backgroundColor;
              }
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {filters.lat ? 'Position active' : 'Utiliser ma position'}
          </button>
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDeleteClick}
              style={{
                ...styles.bulkDeleteButton,
                ...(selectedIds.length === 0 ? styles.bulkDeleteButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButton.backgroundColor;
                }
              }}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Supprimer ({selectedIds.length})
            </button>
          )}
          <Link
            to="/admin/agencies/form"
            style={styles.createButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
            }}
          >
            Créer une nouvelle agence
          </Link>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={handleClearError}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={handleClearSuccess}
        />
      )}

      {/* Location Filter */}
      {showLocationFilter && (
        <div style={styles.locationFilterBar}>
          <div style={styles.locationInputs}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Latitude</label>
              <input
                type="number"
                step="any"
                value={userLocation?.lat || ''}
                onChange={(e) => setUserLocation(prev => ({ ...prev, lat: parseFloat(e.target.value) }))}
                style={styles.input}
                placeholder="Ex: 48.8566"
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Longitude</label>
              <input
                type="number"
                step="any"
                value={userLocation?.long || ''}
                onChange={(e) => setUserLocation(prev => ({ ...prev, long: parseFloat(e.target.value) }))}
                style={styles.input}
                placeholder="Ex: 2.3522"
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Rayon (km)</label>
              <input
                type="number"
                value={filters.radius}
                onChange={(e) => dispatch(setAgencyFilters({ ...filters, radius: parseInt(e.target.value) }))}
                style={styles.input}
                min="1"
                max="100"
              />
            </div>
            <div style={styles.inputGroup}>
              <button
                onClick={handleLocationApply}
                style={styles.applyLocationButton}
              >
                Appliquer
              </button>
            </div>
            <div style={styles.inputGroup}>
              <button
                onClick={handleClearLocation}
                style={{ ...styles.applyLocationButton, backgroundColor: '#6b7280' }}
              >
                Effacer
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.filterBar}>
        <FilterBar
          filters={[
            {
              type: 'select',
              name: 'entityId',
              label: 'Entité',
              options: [
                { value: '', label: 'Toutes les entités' },
                // Add entity options dynamically
              ],
            },
            {
              type: 'select',
              name: 'isOpen',
              label: 'Statut',
              options: [
                { value: '', label: 'Tous' },
                { value: 'true', label: 'Ouvert' },
                { value: 'false', label: 'Fermé' },
              ],
            },
            {
              type: 'select',
              name: 'minRating',
              label: 'Note minimum',
              options: [
                { value: '', label: 'Toutes' },
                { value: '1', label: '1 étoile' },
                { value: '2', label: '2 étoiles' },
                { value: '3', label: '3 étoiles' },
                { value: '4', label: '4 étoiles' },
              ],
            },
            {
              type: 'text',
              name: 'city',
              label: 'Ville',
              placeholder: 'Filtrer par ville...',
            },
            {
              type: 'text',
              name: 'state',
              label: 'Région',
              placeholder: 'Filtrer par région...',
            },
          ]}
          onFilterChange={handleFilterChange}
          initialValues={filters}
        />
      </div>

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher des agences par nom, adresse, ville ou téléphone..." 
          fullWidth
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCellCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === agencies.length && agencies.length > 0}
                  onChange={handleSelectAll}
                  style={styles.checkbox}
                />
              </th>
              <th style={styles.tableHeaderCell}>Agence</th>
              <th style={styles.tableHeaderCell}>Statut</th>
              <th style={styles.tableHeaderCell}>Note</th>
              <th style={styles.tableHeaderCell}>Adresse</th>
              <th style={styles.tableHeaderCell}>Contact</th>
              <th style={styles.tableHeaderCell}>Services</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agencies.map((agency) => (
              <tr 
                key={agency.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCellCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(agency.id)}
                    onChange={() => handleSelectOne(agency.id)}
                    style={styles.checkbox}
                  />
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.agencyInfo}>
                    <div style={styles.agencyAvatar}>
                      {agency.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={styles.agencyName}>{agency.name}</div>
                      {agency.entity && (
                        <div style={styles.entityName}>{agency.entity.name}</div>
                      )}
                      {filters.lat && filters.long && agency.distance && (
                        <div style={styles.distance}>
                          À {agency.distance.toFixed(1)} km
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span
                    style={{
                      ...styles.badge,
                      ...(agency.isOpen ? styles.openBadge : styles.closedBadge),
                    }}
                  >
                    {agency.isOpen ? 'Ouvert' : 'Fermé'}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.rating}>
                    <span style={styles.stars}>
                      {'★'.repeat(Math.floor(agency.rating || 0))}
                      {'☆'.repeat(5 - Math.floor(agency.rating || 0))}
                    </span>
                    <span>{agency.rating?.toFixed(1) || 'N/A'}</span>
                    <span style={styles.reviewCount}>({agency.reviewCount || 0})</span>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.location}>
                    <div>{agency.address}</div>
                    <div>{agency.city} {agency.zipCode}</div>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div>
                    {agency.phone && <div>📞 {agency.phone}</div>}
                    {agency.email && <div>✉️ {agency.email}</div>}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.services}>
                    {agency.services?.slice(0, 3).map(service => (
                      <span key={service.id} style={styles.serviceTag}>
                        {service.name}
                      </span>
                    ))}
                    {agency.services?.length > 3 && (
                      <span style={styles.serviceTag}>
                        +{agency.services.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/agencies/${agency.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="Voir les détails"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/agencies/${agency.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Modifier"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/agencies/${agency.id}/services`}
                      style={{
                        ...styles.actionButton,
                        ...styles.servicesButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.servicesButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.servicesButton.color;
                      }}
                      title="Gérer les services"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/agencies/${agency.id}/hours`}
                      style={{
                        ...styles.actionButton,
                        ...styles.hoursButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.hoursButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.hoursButton.color;
                      }}
                      title="Gérer les horaires"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleToggleStatus(agency)}
                      style={{
                        ...styles.actionButton,
                        ...styles.toggleButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.toggleButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.toggleButton.color;
                      }}
                      title={agency.isOpen ? 'Fermer' : 'Ouvrir'}
                    >
                      {agency.isOpen ? (
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      ) : (
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>

                    <button
                      onClick={() => handleDeleteClick(agency)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Supprimer"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {agencies.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucune agence trouvée correspondant à vos critères
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'agence"
        message={`Êtes-vous sûr de vouloir supprimer l'agence "${selectedAgency?.name}" ? Cette action supprimera également tous les documents associés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Supprimer plusieurs agences"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} agence(s) ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default AgencyList;